/**
 * @file activityPing.js
 * @description The one "this person is in the app" signal (owner 2026-10-30). While a signed-in person has the app
 *   open and visible, it calls touch_activity() about every 2 minutes, and again when they come back to the tab. The
 *   server stamps public.user_activity (throttled to once a minute) and, for an active teammate, the Team page's
 *   "last active" (15 minute throttle, as team_touch did).
 *
 *   send-unread-message-emails reads user_activity to hold the "you have unread messages" email while someone is in
 *   the app (5 minute window, mirrored here by an interval well inside it). Best effort: a failed ping is ignored, it
 *   only makes an email go out sooner.
 * @module lib/activityPing
 */
import { supabase } from "./supabase";

/** How often a visible app pings. Must stay well inside the server's 5 minute "in the app" window. */
export const ACTIVITY_PING_INTERVAL_MS = 2 * 60 * 1000;

/**
 * Starts pinging for the signed-in person: once now, then every intervalMs while the tab is visible, and when they
 * come back to the tab. Returns a cleanup.
 */
export function startActivityPing({ intervalMs = ACTIVITY_PING_INTERVAL_MS } = {}) {
  let last = 0;
  const touch = () => {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
    // Slightly under the interval so the timer's own tick is never skipped.
    if (Date.now() - last < intervalMs - 5000) return;
    last = Date.now();
    Promise.resolve(supabase.rpc("touch_activity")).catch(() => {});
  };
  touch();
  const timer = setInterval(touch, intervalMs);
  const onVisible = () => touch();
  document.addEventListener("visibilitychange", onVisible);
  return () => { clearInterval(timer); document.removeEventListener("visibilitychange", onVisible); };
}
